import { css } from "styled-components";
import { AnyObject } from "../models";

const mobileBreakpoint = "768px",
  tabletBreakpoint = "1024px",
  mobileBreakpointRuleDown = `@media screen and (max-width: ${mobileBreakpoint})`,
  tabletBreakpointRuleDown = `@media screen and (max-width: ${tabletBreakpoint})`;

/**
 * Color object contains base colors, extend named theme colors below:
 * IE:
 * const color = {
 *  red: "#FF0000",
 * }
 * color.primary = color.red;
 */
const color: { [key: string]: string } = {
  success: "#00C851",
  info: "#3769E4",
  infoLight: "#F5F9FF",
  infoDark: "#2A57C8",
  warning: "#FFAC26",
  warningLight: "#FFFCF5",
  warningDark: "#E7AF41",
  error: "#C83434",
  errorLight: "#FFF7F7",
  errorDark: "#B12424",
  neutral: "#757575",
  neutralLight: "#FAFAFA",
  neutralDark: "#616161",

  green: "#6BBC19",
  red: "#DD0000",
  gallerySelection: "#E9EEF4",
  bestValueBackground: "#F8F9FE",
  focus: "#5586FF",
  toolbarBackground: "rgba(255, 255, 255, 0.98)",
  modalOverlay: "rgba(42, 42, 42, 0.9)",
  navbarHoverBackground: "rgba(88, 89, 90, 0.50)",

  // Note background colors
  noteInfo: "#FEF2CD",
  noteWarning: "#F1CCCC",
  noteSuccess: "#DBEAD4",
  noteNeutral: "#FAFAFA",
  //TODO: should we refactor to remove above?
  noteInfoColor: "#FEF2CD",
  noteWarningColor: "#F1CCCC",
  noteSuccessColor: "#DBEAD4",
  noteNeutralColor: "#FAFAFA",

  // Brand blue
  blue700: "#3534C4",
  blue600: "#3E3FD1",
  blue500: "#4349DC",
  blue400: "#6066E3",
  blue300: "#6066E3",
  blue200: "#C8C9F5",
  blue100: "#E7E7FF",

  // Brand red
  red700: "#BD1759",
  red600: "#D31A5F",
  red500: "#E41D61",
  red400: "#E73F78",
  red300: "#EC6190",
  red200: "#F18EAF",
  red100: "#C8C9F5",

  // Brand yellow
  yellow700: "#3534c4",
  yellow600: "#3e3fd1",
  yellow500: "#4349dc",
  yellow400: "#6066e3",
  yellow300: "#6066e3",
  yellow200: "#a3a6ef",
  yellow100: "#c8c9f5",

  // Brand neutral
  neutral900: "#242425",
  neutral800: "#58595A",
  neutral700: "#767678",
  neutral600: "#A3A4A6",
  neutral500: "#D4D5D7",
  neutral400: "#E1E2E4",
  neutral300: "#EEEFF0",
  neutral200: "#FAFAFA",
  neutral100: "#FFFFFF",
};

color.primaryDarkest = color.blue700;
color.primaryDark = color.blue600;
color.primary = color.blue500;
color.primaryLight = color.blue400;
color.primaryLighter = color.blue300;
color.primaryExtraLight = color.blue200;
color.primaryLightest = color.blue100;

color.secondaryDarkest = color.red700;
color.secondaryDark = color.red600;
color.secondary = color.red500;
color.secondaryLight = color.red400;
color.secondaryLighter = color.red300;
color.secondaryExtraLight = color.red200;
color.secondaryLightest = color.red100;

color.tertiaryDarkest = color.yellow700;
color.tertiaryDark = color.yellow600;
color.tertiary = color.yellow500;
color.tertiaryLight = color.yellow400;
color.tertiaryLighter = color.yellow300;
color.tertiaryExtraLight = color.yellow200;
color.tertiaryLightest = color.yellow100;

color.black = color.neutral900;
color.grayDarkest = color.neutral800;
color.grayDarker = color.neutral700;
color.grayDark = color.neutral600;
color.gray = color.neutral500;
color.grayLight = color.neutral400;
color.grayLighter = color.neutral300;
color.grayLightest = color.neutral200;
color.white = color.neutral100;

color.neutralLoader = color.neutral600;
color.border = color.neutral400;
color.panelBackground = color.neutral200;

// Asset background colors
color.assetLight = color.neutral300;
color.assetDark = color.neutral600;

const boxShadow = {
  panel: "0 10px 20px rgba(0, 0, 0, 0.2)",
  component: "0px 1px 1px rgba(42, 42, 42, 0.05)",
  card: "0px 4px 16px 0px rgba(42, 42, 42, 0.07);",
  image: "0px 2px 6px rgba(42, 42, 42, 0.14), 0px 6px 20px rgba(0, 0, 0, 0.08);",
  button: "0px 1px 2px rgba(42, 42, 42, 0.12), 0px 2px 8px rgba(42, 42, 42, 0.08);",
};

const borderRadius: { [key: string]: string | Record<string, Record<string, string>> } = {
  //Keeping old "default" for compatibility

  small: "4px",
  medium: "8px",
  large: "18px",

  default: "4px",
  defaultOuter: "6px",
};

borderRadius.themeOverrides = {
  classic: {
    default: "4px",
    defaultOuter: "6px",
  },
  scher: {
    default: "18px", // For most components this is effectiely 50% but we don't want 50% for larget components like textareas and boxes. This works well.
    defaultOuter: "20px",
  },
  wyman: {
    default: "8px",
    defaultOuter: "10px",
  },
};

const border = {
  default: `1px solid ${color.grayLight}`,
  grayDashed: `1px dashed ${color.grayLight}`,
  warning: `1px solid rgba(255, 172, 38, 0.6)`,
};

const zIndex = {
  inspectorView: 900,
  assetOverlay: 1000,
  modalWrapper: 20000,
  modal: 999,
  tooltip: 51001,
  popupMenu: 51000,
};

export const fontStyles = {
  s76: {
    fontSize: "76px",
    lineHeight: "96px",
    [mobileBreakpointRuleDown]: {
      fontSize: "48px",
      lineHeight: "64px",
    },
  },
  s48: {
    fontSize: "48px",
    lineHeight: "64px",
    [mobileBreakpointRuleDown]: {
      fontSize: "28px",
      lineHeight: "40px",
    },
  },
  s36: {
    fontSize: "36px",
    lineHeight: "56px",
  },
  s28: {
    fontSize: "28px",
    lineHeight: "40px",
    [mobileBreakpointRuleDown]: {
      fontSize: "24px",
      lineHeight: "32px",
    },
  },
  s22: {
    fontSize: "22px",
    lineHeight: "32px",
    [mobileBreakpointRuleDown]: {
      fontSize: "20px",
      lineHeight: "32px",
    },
  },
  s20: {
    fontSize: "20px",
    lineHeight: "24px",
  },
  s18: {
    fontSize: "18px",
    lineHeight: "24px",
  },
  s16: {
    fontSize: "16px",
    lineHeight: "24px",
  },
  s14: {
    fontSize: "14px",
    lineHeight: "24px",
  },
  s12: {
    fontSize: "12px",
    lineHeight: "16px",
  },
  s11: {
    fontSize: "11px",
    lineHeight: "16px",
  },
  s9: {
    fontSize: "9px",
    lineHeight: "12px",
  },
  italic: {
    fontStyle: "italic",
  },
};

const fontWeight = {
  light: {
    fontWeight: 300,
  },
  regular: {
    fontWeight: "normal",
  },
  medium: {
    fontWeight: 500,
  },
  semibold: {
    fontWeight: 600,
  },
  bold: {
    fontWeight: 700,
  },
  extraBold: {
    fontWeight: 800,
  },
  black: {
    fontWeight: 900,
  },
};

export const fontFamily = {
  inter: {
    fontFamily: "'Inter', sans-serif",
  },
  mono: {
    fontFamily: "'Source Code Pro', monospace",
  },
};

/**
 * The font object contains all styles both as nested groups for access in code
 * as well as dot seperated strings for access via component props
 *
 * Access via code
 * ```
 * import { font } from './styles'
 * const myStyle = css`
 *  ${font.ui.title}
 * `
 * ```
 *
 * Access via props
 * ```
 * <MyComponent font="ui.title" />
 * ```
 */
const font: { [key: string]: AnyObject } = {};
font.ui = {
  /** Marketing and 404 */
  mediumTitle: { ...fontStyles.s48, ...fontWeight.bold, ...fontFamily.inter },
  pageTitle: { ...fontStyles.s28, ...fontWeight.extraBold, ...fontFamily.inter },
  title: { ...fontStyles.s20, ...fontWeight.extraBold, ...fontFamily.inter },
  subtitle: { ...fontStyles.s18, ...fontWeight.light, ...fontFamily.inter },
  mediumSubtitle: { ...fontStyles.s28, ...fontWeight.medium, ...fontFamily.inter },
  regular: { ...fontStyles.s14, ...fontWeight.regular, ...fontFamily.inter },
  regularSemiBold: { ...fontStyles.s14, ...fontWeight.semibold, ...fontFamily.inter },
  regularBold: { ...fontStyles.s14, ...fontWeight.bold, ...fontFamily.inter },
  small: { ...fontStyles.s12, ...fontWeight.regular, ...fontFamily.inter },
  smallBold: { ...fontStyles.s12, ...fontWeight.semibold, ...fontFamily.inter },
  tiny: { ...fontStyles.s11, ...fontWeight.regular, ...fontFamily.inter },
  extraTiny: { ...fontStyles.s9, ...fontWeight.regular, ...fontFamily.inter },
};
font.themeOverrides = {
  scher: {
    "ui.mediumTitle": {
      fontSize: "76px",
      letterSpacing: "-.03em",
      [mobileBreakpointRuleDown]: { fontSize: "40px" },
    },
    "ui.mediumSubtitle": {
      fontSize: "48px",
      letterSpacing: "-.02em",
      [mobileBreakpointRuleDown]: { fontSize: "32px" },
    },
    "ui.pageTitle": {
      fontSize: "28px",
      letterSpacing: "-.02em",
      [mobileBreakpointRuleDown]: { fontSize: "28px" },
    },
    "ui.title": {
      fontSize: "24px",
      lineHeight: "28px",
    },
    "ui.subtitle": {
      fontSize: "24px",
      [mobileBreakpointRuleDown]: { fontSize: "20px" },
    },
    "gallery.title": {
      fontSize: "48px",
      fontWeight: 600,
      letterSpacing: "-0.021em",
      lineHeight: 1,
    },
  },
  wyman: {
    "ui.mediumTitle": {
      fontSize: "52px",
      fontWeight: 700,
      letterSpacing: "-.015em",
      [mobileBreakpointRuleDown]: { fontSize: "36px" },
    },
    "ui.mediumSubtitle": {
      letterSpacing: "-.015em",
    },
    "ui.pageTitle": {
      fontSize: "24px",
      fontWeight: 600,
      letterSpacing: "-.015em",
      [mobileBreakpointRuleDown]: { fontSize: "24px" },
    },
    "ui.title": {
      fontWeight: 500,
      letterSpacing: "-.015em",
    },
    "ui.subtitle": { fontWeight: 400, letterSpacing: "-.015em" },
  },
};
font.classic = {
  title: { ...font.ui.title, fontWeight: 600, letterSpacing: "-.0125em", lineHeight: 1.5 },
};
font.gallery = {
  /** Main gallery tites (e.g. section name) */
  title: { ...fontStyles.s36, ...fontWeight.black, ...fontFamily.inter },
  /** Heading items */
  heading: { ...fontStyles.s28, ...fontWeight.semibold, ...fontFamily.inter },
  /** Markdown h1 */
  h1: { ...fontStyles.s22, ...fontWeight.semibold, ...fontFamily.inter },
  /** Markdown h2 */
  h2: { ...fontStyles.s16, ...fontWeight.semibold, ...fontFamily.inter },
  /** Markdown body */
  regular: { ...fontStyles.s14, ...fontWeight.regular, ...fontFamily.inter },
  /** Code */
  code: { ...fontStyles.s14, ...fontFamily.mono },
};

/**
 * Marketing fonts
 */
font.marketing = {
  /** H1 Text */
  h1: {
    fontSize: "44px",
    lineHeight: "120%",
    letterSpacing: "-1.5px",
    [mobileBreakpointRuleDown]: {
      fontSize: "36px",
      lineHeight: "48px",
      letterSpacing: "-1.25px",
    },
    ...fontWeight.medium,
    ...fontFamily.inter,
  },
  /** H2 Text */
  h2: {
    fontSize: "32px",
    lineHeight: "140%",
    letterSpacing: "0",
    ...fontWeight.medium,
    ...fontFamily.inter,
    [mobileBreakpointRuleDown]: {
      lineHeight: "120%",
    },
  },
  /** H3 Text */
  h3: {
    fontSize: "28px",
    lineHeight: "120%",
    letterSpacing: "0",
    ...fontWeight.medium,
    ...fontFamily.inter,
    [mobileBreakpointRuleDown]: {
      lineHeight: "140%",
    },
  },
  /** H4 Text */
  h4: {
    fontSize: "24px",
    lineHeight: "140%",
    letterSpacing: "0",
    ...fontWeight.bold,
    ...fontFamily.inter,
    [mobileBreakpointRuleDown]: {
      lineHeight: "120%",
      ...fontWeight.medium,
    },
  },
  /** H5 Text */
  h5: {
    fontSize: "20px",
    lineHeight: "140%",
    letterSpacing: "0",
    ...fontWeight.medium,
    ...fontFamily.inter,
    [mobileBreakpointRuleDown]: {
      fontSize: "16px",
    },
  },
  /** Body Large */
  bodyLarge: {
    fontSize: "20px",
    lineHeight: "32px",
    letterSpacing: "-0.25px",
    ...fontWeight.light,
    ...fontFamily.inter,
  },
  /** Body Regular */
  regular: {
    fontSize: "16px",
    lineHeight: "160%",
    letterSpacing: "0px",
    ...fontWeight.regular,
    ...fontFamily.inter,
  },
  /** Body Regular Bold */
  regularBold: {
    fontSize: "16px",
    lineHeight: "120%",
    letterSpacing: "0px",
    ...fontWeight.bold,
    ...fontFamily.inter,
  },

  semiBold: {
    fontSize: "16px",
    lineHeight: "120%",
    letterSpacing: "0px",
    ...fontWeight.semibold,
    ...fontFamily.inter,
  },

  /** Nav Link */
  navLink: {
    fontSize: "16px",
    lineHeight: "120%",
    ...fontWeight.regular,
    ...fontFamily.inter,
  },
  eyebrow: {
    fontSize: "20px",
    lineHeight: "140%",
    letterSpacing: "0",
    ...fontWeight.medium,
    ...fontFamily.inter,
  },
  xl: {
    fontSize: "56px",
    lineHeight: "120%",
    letterSpacing: "-1.5px",
    ...fontWeight.medium,
    ...fontFamily.inter,
  },
  xxl: {
    fontSize: "80px",
    lineHeight: "120%",
    letterSpacing: "-1.5px",
    ...fontWeight.semibold,
    ...fontFamily.inter,
  },
  /** Nav Link */
  textLink: {
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: ".5px",
    ...fontWeight.semibold,
    ...fontFamily.inter,
  },
  /** Caption */
  caption: {
    fontSize: "11px",
    lineHeight: "16px",
    ...fontWeight.regular,
    ...fontFamily.inter,
  },
};

font.blog = {
  h1: {
    fontSize: "32px",
    lineHeight: "48px",
    letterSpacing: ".25px",
    [mobileBreakpointRuleDown]: {
      fontSize: "28px",
      lineHeight: "42px",
      letterSpacing: ".25px",
    },
    ...fontWeight.bold,
    ...fontFamily.inter,
  },
  h2: {
    fontSize: "24px",
    lineHeight: "36px",
    letterSpacing: "0.25px",
    [mobileBreakpointRuleDown]: {
      fontSize: "20px",
      lineHeight: "30px",
      letterSpacing: "0.25px",
    },
    ...fontWeight.bold,
    ...fontFamily.inter,
  },
  h3: {
    fontSize: "20px",
    lineHeight: 1.5,
    letterSpacing: "0.25px",
    [mobileBreakpointRuleDown]: {
      fontSize: "20px",
      lineHeight: 1.5,
      letterSpacing: "0.25px",
    },
    ...fontWeight.bold,
    ...fontFamily.inter,
  },
  figcaption: {
    fontSize: "11px",
    lineHeight: 1.2,
    letterSpacing: "0.08px",
    ...fontWeight.regular,
    ...fontFamily.inter,
  },
};

Object.keys(font).forEach(groupKey => {
  const g = font[groupKey];
  Object.keys(g).forEach(styleKey => {
    font[`${groupKey}.${styleKey}`] = font[groupKey][styleKey];
  });
});

const space = {
  none: "0",
  xxs: "2px",
  xs: "4px",
  s: "8px",
  m: "16px",
  l: "24px",
  xl: "40px",
  xxl: "80px",
  xxxl: "120px",
};

const Devices = {
  mobile: 0,
  tablet: 1,
  desktop: 2,

  mediaQueryForDevice: device => {
    switch (device) {
      case Devices.mobile:
        return mobileBreakpointRuleDown;
      case Devices.tablet:
        return tabletBreakpointRuleDown;
      default:
        return `@media(min-width: ${tabletBreakpoint})`;
    }
  },
};

/**
 * Spacing values for responsive layouts
 *
 * [<768, 769-1024, >1024]
 */
const spacing: Record<string, number[]> = {
  none: [0, 0, 0],
  xxs: [2, 2, 2],
  xs: [4, 4, 4],
  s: [4, 4, 8],
  m: [12, 14, 16],
  l: [16, 24, 24],
  xl: [24, 32, 40],
  xxl: [40, 60, 80],
  xxxl: [80, 100, 120],
};

Object.keys(spacing).forEach(k => {
  const negValues = spacing[k].map(v => -Math.abs(v));
  spacing[`-${k}`] = negValues;
});

const properties = {
  a: [""],
  t: ["Top"],
  b: ["Bottom"],
  r: ["Right"],
  l: ["Left"],
  x: ["Left", "Right"],
  y: ["Top", "Bottom"],
};

const makeSpacingRule = (propertyType: string, propertyAspects: string[], spacingKey: string) => {
  const [mobilePx, tabletPx, desktopPx] = spacing[spacingKey];
  return {
    ...propertyAspects.reduce((obj, asp) => {
      obj[`${propertyType}${asp}`] = `${desktopPx}px`;
      return obj;
    }, {}),
    [tabletBreakpointRuleDown]: {
      ...propertyAspects.reduce((obj, asp) => {
        obj[`${propertyType}${asp}`] = `${tabletPx}px`;
        return obj;
      }, {}),
    },
    [mobileBreakpointRuleDown]: {
      ...propertyAspects.reduce((obj, asp) => {
        obj[`${propertyType}${asp}`] = `${mobilePx}px`;
        return obj;
      }, {}),
    },
  };
};

/**
 * These are populated as
 * {
 *  a: {
 *    none: { margin: 0, @media(...) }
 *    xxs: { margin: 2, @media(...) }
 *  },
 *  t: {
 *    none: { margin-top: 0, @media(...) }
 *    xxs: { margin: 2, @media(...) }
 *  },
 * }
 */
const padding: { [key: string]: AnyObject } = {};
const margin: { [key: string]: AnyObject } = {};

Object.entries(properties).forEach(p => {
  const pKey = p[0],
    pVal = p[1];
  padding[pKey] = {};
  margin[pKey] = {};

  Object.keys(spacing).forEach(spacingKey => {
    padding[pKey][spacingKey] = makeSpacingRule("padding", pVal, spacingKey);
    margin[pKey][spacingKey] = makeSpacingRule("margin", pVal, spacingKey);
  });
});

/**
 * Defined breakpoints to use for styled-component media query helper
 */
const bp = {
  xs: 500,
  s: 768,
  m: 1024,
  l: 1200,
  xl: 1400,
};

/**
 * Creates declarative media queries for use within styled components
 * Ex:
 * const Wrapper = styled.div`
 *  background: black;
 *  ${mq.s`
 *    background: red;
 *  `}
 *  ${mq.l_up`
 *    background: blue;
 *  `}
 * `
 */

export type MediaQueries = {
  xs: typeof css;
  s: typeof css;
  m: typeof css;
  l: typeof css;
  xl: typeof css;
  xs_up: typeof css;
  s_up: typeof css;
  m_up: typeof css;
  l_up: typeof css;
  xl_up: typeof css;
};

export {
  padding,
  margin,
  color,
  boxShadow,
  font,
  zIndex,
  spacing,
  borderRadius,
  space,
  border,
  Devices,
  bp,
};
